// Utilidades de fecha con zona horaria fija: Ciudad de México (America/Mexico_City).
// Evita desfases de día/hora causados por `Date#toISOString()`, que convierte a UTC.

const MEXICO_TIME_ZONE = 'America/Mexico_City';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function partsFor(date: Date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MEXICO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
}

// Fecha actual en CDMX como 'YYYY-MM-DD'.
export function todayMexico(): string {
  const parts = partsFor(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Timestamp completo (fecha y hora) actual en CDMX, en formato
// 'YYYY-MM-DDTHH:mm:ss' sin sufijo de zona. Útil para marcas de tiempo
// (p. ej. sent_at) que deben reflejar la hora local de la Ciudad de México
// y NO el UTC del navegador o del servidor.
export function nowMexicoISO(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MEXICO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  let hour = get('hour');
  // Intl puede devolver '24' para la medianoche; lo normalizamos a '00'.
  if (hour === '24') hour = '00';
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}:${get('second')}`;
}

// Fecha de hoy en CDMX formateada en texto largo (es-MX), p. ej.
// "lunes, 8 de septiembre de 2026". Evita que el encabezado dependa de la
// zona horaria del navegador.
export function todayMexicoLong(): string {
  return new Intl.DateTimeFormat('es-MX', {
    timeZone: MEXICO_TIME_ZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date());
}

// Formatea un Date como 'YYYY-MM-DD' en CDMX (útil para timestamps).
export function dateToMexicoStr(date: Date): string {
  const parts = partsFor(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

// Devuelve la hora actual (0-23) en la zona horaria de la Ciudad de México.
export function hourMexico(): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: MEXICO_TIME_ZONE,
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  let hour = Number(parts.find((p) => p.type === 'hour')?.value || '0');
  // Intl puede devolver '24' para la medianoche; lo normalizamos a '0'.
  if (hour === 24) hour = 0;
  return hour;
}

// Saludo dinámico según la hora local de la Ciudad de México (America/Mexico_City):
//   00:00 a 11:59 -> "buenos días"
//   12:00 a 18:59 -> "buenas tardes"
//   19:00 a 23:59 -> "buenas noches"
export function getDynamicGreeting(): string {
  const hour = hourMexico();
  if (hour < 12) return 'buenos días';
  if (hour < 19) return 'buenas tardes';
  return 'buenas noches';
}

// Suma días a una fecha 'YYYY-MM-DD' sin desfase de zona horaria.
export function addDays(dateStr: string, days: number): string {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Suma el periodo del ciclo de facturación a una fecha 'YYYY-MM-DD'.
// Devuelve null para 'one_time'.
export function addBillingPeriod(dateStr: string, cycle: string): string | null {
  if (cycle === 'one_time' || !dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  switch (cycle) {
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'quarterly':
      date.setMonth(date.getMonth() + 3);
      break;
    case 'semiannual':
    case 'semi_annual':
      date.setMonth(date.getMonth() + 6);
      break;
    case 'annual':
      date.setFullYear(date.getFullYear() + 1);
      break;
    default:
      return null;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Último día del mes de una fecha 'YYYY-MM-DD' (p. ej. '2026-10-07' -> '2026-10-31').
// Se usa como límite superior de la ventana de facturación proactiva del mes en curso.
export function endOfMonth(dateStr: string): string {
  if (!dateStr) return dateStr;
  const [y, m] = dateStr.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return `${y}-${pad(m)}-${pad(lastDay)}`;
}

// Coloca una fecha 'YYYY-MM-DD' dentro de un mes destino ('YYYY-MM'), conservando
// el día original (con recorte al último día del mes si no existe). Sirve para
// realinear silenciosamente una suscripción que quedó desfasada a un mes futuro.
export function keepDayInMonth(dateStr: string, targetMonth: string): string {
  if (!dateStr || !targetMonth) return dateStr;
  const day = Number(dateStr.split('-')[2]) || 1;
  const [y, m] = targetMonth.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const safeDay = Math.min(Math.max(day, 1), lastDay);
  return `${y}-${pad(m)}-${pad(safeDay)}`;
}

// Devuelve el inicio del periodo de facturación actual (YYYY-MM-DD) para una fecha y ciclo.
export function periodStart(dateStr: string, cycle: string): string {
  if (!dateStr) return dateStr;
  const [y, m] = dateStr.split('-').map(Number);
  switch (cycle) {
    case 'monthly':
      return `${y}-${pad(m)}-01`;
    case 'quarterly': {
      const qStartMonth = Math.ceil(m / 3) * 3 - 2;
      return `${y}-${pad(qStartMonth)}-01`;
    }
    case 'semi_annual':
      return `${y}-${m <= 6 ? '01' : '07'}-01`;
    case 'annual':
      return `${y}-01-01`;
    default:
      return `${y}-${pad(m)}-01`;
  }
}

// Días transcurridos entre dos fechas 'YYYY-MM-DD' (b - a, puede ser negativo).
export function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const d1 = new Date(ay, am - 1, ad);
  const d2 = new Date(by, bm - 1, bd);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

// Próxima fecha de corte (día del mes = cutDay) mayor o igual a fromDate.
export function nextCutDate(cutDay: number, fromDate: string): string {
  if (!fromDate) return fromDate;
  const [y, m, d] = fromDate.split('-').map(Number);
  const from = new Date(y, m - 1, d);
  let cut = new Date(y, m - 1, cutDay);
  if (cut < from) cut = new Date(y, m, cutDay);
  return `${cut.getFullYear()}-${pad(cut.getMonth() + 1)}-${pad(cut.getDate())}`;
}

// Prorrateo de un concepto: (precio unitario / 30) * días transcurridos.
export function calcProration(unitPrice: number, days: number): number {
  return (unitPrice / 30) * Math.max(0, days);
}

// Abreviaturas de mes en español para etiquetas compactas de prorrateo.
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Formatea una fecha 'YYYY-MM-DD' como 'dd-mmm' (p. ej. '2026-10-07' -> '07-oct').
export function formatDayMonth(dateStr: string): string {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-').map(Number);
  const month = MESES_CORTOS[(m || 1) - 1] || '';
  return `${pad(d)}-${month}`;
}